<template>
  <div class="home-container">
    <!-- 顶部欢迎语 -->
    <div class="welcome-section">
      <div class="welcome-text">
        <h1>您好，{{ currentUser?.name }}，祝你工作顺利！</h1>
        <p>{{ today }} | {{ weekDay }}</p>
      </div>
      <div class="quick-stats">
        <div v-for="stat in quickStats" :key="stat.label" class="stat-item">
          <div class="stat-value">{{ stat.value }}</div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>

    <!-- 数据图表 -->
    <el-row :gutter="20" class="mt-6">
      <el-col :xs="24" :sm="24" :md="12">
        <div class="chart-card">
          <div class="chart-header">
            <span class="chart-title">设备状态分布</span>
          </div>
          <EquipmentStatusChart :chart-data="statusData" />
        </div>
      </el-col>
      <el-col :xs="24" :sm="24" :md="12">
        <div class="chart-card">
          <div class="chart-header">
            <span class="chart-title">热门设备分类</span>
          </div>
          <PopularCategoryChart :chart-data="categoryData" />
        </div>
      </el-col>
    </el-row>

    <!-- 工作台主要内容 -->
    <el-row :gutter="20" class="mt-6">
      <!-- 左侧：快捷入口和待办任务 -->
      <el-col :xs="24" :sm="24" :md="16">
        <!-- 快捷入口 -->
        <div class="workplace-card mb-6">
          <div class="card-header">
            <span class="header-title">
              <el-icon class="mr-8"><Grid /></el-icon>
              常用应用
            </span>
          </div>
          <div class="app-grid">
            <div v-for="app in apps" :key="app.id || app.name" class="app-item" @click="$router.push(app.path)">
              <div class="app-icon" :style="{ backgroundColor: app.color }">
                <span
                  v-if="canManageBusiness"
                  class="delete-btn"
                  title="删除该业务（无历史申请记录时才可删除）"
                  @click.stop="handleDeleteApp(app)"
                >×</span>
                <el-icon :size="22" color="#fff">
                  <component :is="app.icon" v-if="app.icon" />
                  <Menu v-else />
                </el-icon>
              </div>
              <span class="app-name">{{ app.name }}</span>
            </div>
            <!-- 管理员专属入口 -->
            <div v-if="currentUser?.permissions?.includes('config_business')" class="app-item" @click="$router.push('/business-config')">
              <div class="app-icon" style="background-color: #3370ff">
                <el-icon :size="22" color="#fff"><Setting /></el-icon>
              </div>
              <span class="app-name">业务配置</span>
            </div>
          </div>
        </div>

        <!-- 我的待办任务 -->
        <div class="workplace-card">
          <div class="card-header">
            <span class="header-title">
              <el-icon class="mr-8"><Checked /></el-icon>
              我的待办任务
            </span>
            <span class="more-link" @click="$router.push('/tasks')">全部任务 <el-icon><ArrowRight /></el-icon></span>
          </div>
          <div class="task-list" v-loading="loading">
            <template v-if="myTasks.length > 0">
              <div v-for="task in myTasks" :key="task.id" class="task-item" @click="$router.push('/tasks')">
                <div class="task-status-bar" :class="task.priority"></div>
                <div class="task-info">
                  <div class="task-title">{{ task.title }}</div>
                  <div class="task-meta">
                    <span class="meta-icon"><el-icon><Clock /></el-icon></span>
                    截止: {{ task.due_date || '未设置' }}
                  </div>
                </div>
                <el-tag size="small" :type="task.status === 'in_progress' ? 'primary' : 'info'" class="task-tag">
                  {{ task.status === 'in_progress' ? '进行中' : '待处理' }}
                </el-tag>
              </div>
            </template>
            <div v-else class="empty-placeholder">
              <el-icon :size="40" color="#c0c4cc"><CircleCheck /></el-icon>
              <p>今日无紧急任务</p>
            </div>
          </div>
        </div>
      </el-col>

      <!-- 右侧：待审批和消息 -->
      <el-col :xs="24" :sm="24" :md="8">
        <!-- 待我审批 / 我的申请 -->
        <div class="workplace-card mb-6">
          <div class="card-header">
            <span class="header-title">
              <el-icon class="mr-8"><CircleCheck /></el-icon>
              {{ isApprover ? '待我审批' : '我的申请' }}
            </span>
            <span class="more-link" @click="$router.push('/approvals')">{{ isApprover ? '去处理' : '查看' }} <el-icon><ArrowRight /></el-icon></span>
          </div>
          <div class="list-container">
            <template v-if="pendingApprovals.length > 0">
              <div v-for="app in pendingApprovals" :key="app.id" class="list-item" @click="$router.push('/approvals')">
                <el-avatar :size="36" class="avatar">{{ (isApprover ? (app.applicant_name || '无') : (app.approver_name || '无')).charAt(0) }}</el-avatar>
                <div class="item-content">
                  <div class="item-title">{{ app.title }}</div>
                  <div class="item-desc">
                    <el-tag size="small" :type="getStatusTagType(app.status)" class="mr-8">
                      {{ getStatusLabel(app.status) }}
                    </el-tag>
                    {{ isApprover ? '来自 ' + app.applicant_name : '审批人: ' + app.approver_name }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="empty-placeholder">
              <el-icon :size="40" color="#c0c4cc"><DocumentChecked /></el-icon>
              <p>{{ isApprover ? '暂无待审批项' : '暂无申请中项目' }}</p>
            </div>
          </div>
        </div>

        <!-- 最近消息 -->
        <div class="workplace-card">
          <div class="card-header">
            <span class="header-title">
              <el-icon class="mr-8"><ChatDotRound /></el-icon>
              最近消息
            </span>
            <span class="more-link" @click="$router.push('/messages')">进入中心 <el-icon><ArrowRight /></el-icon></span>
          </div>
          <div class="list-container">
            <template v-if="recentMessages.length > 0">
              <div v-for="msg in recentMessages" :key="msg.id" class="list-item" @click="$router.push('/messages')">
                <div class="item-content">
                  <div class="item-title" :class="{ unread: !msg.is_read }">{{ msg.title }}</div>
                  <div class="item-desc">{{ msg.content }}</div>
                </div>
                <span class="item-time">{{ formatTime(msg.created_at) }}</span>
              </div>
            </template>
            <div v-else class="empty-placeholder">
              <el-icon :size="40" color="#c0c4cc"><Bell /></el-icon>
              <p>暂无新消息</p>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { 
  Grid, ChatDotRound, Checked, CircleCheck, 
  Monitor, FolderOpened, Calendar, Tools, 
  Collection, Tickets, Setting, Menu, Close,
  Clock, ArrowRight, DocumentChecked, Bell
} from '@element-plus/icons-vue'
import api from '@/api'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { ElMessage, ElMessageBox } from 'element-plus'
import EquipmentStatusChart from '@/components/charts/EquipmentStatusChart.vue'
import PopularCategoryChart from '@/components/charts/PopularCategoryChart.vue'

dayjs.locale('zh-cn')

const currentUser = ref(null)
const loading = ref(false)
const myTasks = ref([])
const pendingApprovals = ref([])
const recentMessages = ref([])
const totalEquipments = ref(0)
const activeBorrows = ref(0)

const statusData = ref([])
const categoryData = ref([])

const today = computed(() => dayjs().format('YYYY年MM月DD日'))
const weekDay = computed(() => dayjs().format('dddd'))

const isApprover = computed(() => currentUser.value?.permissions?.includes('approve'))
const canManageBusiness = computed(() => currentUser.value?.permissions?.includes('config_business'))

const pendingApprovalsCount = ref(0)
const unreadMessagesCount = ref(0)

const handleMessageUpdated = async () => {
  try {
    const uid = currentUser.value?.id
    if (!uid) return
    const msgRes = await api.getMessages(uid)
    const msgData = msgRes.data.results || msgRes.data || []
    unreadMessagesCount.value = msgData.filter((m) => !m.is_read).length
  } catch (error) {
    console.error('更新消息统计失败', error)
  }
}

const quickStats = computed(() => [
  { label: '在库设备', value: totalEquipments.value },
  { label: '我的待办', value: myTasks.value.length },
  { label: isApprover.value ? '待我审批' : '我的申请', value: pendingApprovalsCount.value },
  { label: '消息通知', value: unreadMessagesCount.value }
])

const apps = ref([])

const fetchApps = async () => {
  try {
    const res = await api.getLowCodeApps()
    apps.value = res.data.map(app => ({
      id: app.id,
      name: app.name,
      icon: app.icon || 'Menu',
      path: `/lowcode/${app.code}`,
      color: app.color || '#909399'
    }))
  } catch (error) {
    console.error('Failed to fetch apps:', error)
  }
}

const handleDeleteApp = async (app) => {
  try {
    await ElMessageBox.confirm(
      `确定删除业务「${app.name}」吗？仅当该业务下没有任何申请记录时才能删除；若有历史数据，请先到业务配置中停用或清理后再试。`,
      '删除业务',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    await api.deleteLowCodeApp(app.id)
    ElMessage.success('删除成功')
    await fetchApps()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to delete app:', error)
      ElMessage.error(api.getErrorMessage(error, '删除失败'))
    }
  }
}

const fetchData = async () => {
  loading.value = true
  try {
    const profileId = currentUser.value?.id
    
    let params = {}
    if (isApprover.value) {
      params.status = 'pending'
      params.approver = profileId
    } else {
      params.status__in = 'pending,approved' 
      params.applicant = profileId
    }

    let equipRes, taskRes, approvalRes, msgRes, statsRes
    try {
      equipRes = await api.getEquipments()
    } catch(e) { console.error('[设备列表]失败', e); throw new Error('[设备列表] /api/equipments/ 失败: ' + (e?.message || '')) }
    try {
      taskRes = await api.getTasks({ assignee: profileId, status: 'pending,in_progress' })
    } catch(e) { console.error('[任务列表]失败', e); throw new Error('[任务列表] /api/core/tasks/ 失败: ' + (e?.message || '')) }
    try {
      approvalRes = await api.getApprovals(params)
    } catch(e) { console.error('[审批列表]失败', e); throw new Error('[审批列表] /api/core/approvals/ 失败: ' + (e?.message || '')) }
    try {
      msgRes = await api.getMessages(profileId)
    } catch(e) { console.error('[消息列表]失败', e); throw new Error('[消息列表] /api/core/messages/ 失败: ' + (e?.message || '')) }
    try {
      statsRes = await api.getEquipmentStats()
    } catch(e) { console.error('[设备统计]失败', e); throw new Error('[设备统计] /api/equipments/stats/ 失败: ' + (e?.message || '')) }

    totalEquipments.value = (equipRes.data.results || equipRes.data || []).length
    myTasks.value = (taskRes.data.results || taskRes.data || []).filter(t => t.status !== 'completed').slice(0, 5)
    
    const approvalData = approvalRes.data.results || approvalRes.data || []
    pendingApprovals.value = approvalData.slice(0, 5)
    pendingApprovalsCount.value = approvalRes.data.count || approvalData.length
    
    const msgData = msgRes.data.results || msgRes.data || []
    recentMessages.value = msgData.slice(0, 5)
    unreadMessagesCount.value = msgData.filter((m) => !m.is_read).length
    
    if (statsRes.data) {
      statusData.value = statsRes.data.status_distribution || []
      categoryData.value = statsRes.data.popular_categories || []
    }
  } catch (error) {
    console.error('加载工作台数据失败', error)
    ElMessage.error(error.message || '工作台数据加载失败，请检查网络或重新登录')
  } finally {
    loading.value = false
  }
}

const getStatusLabel = (status) => {
  const map = {
    pending: '待处理',
    approved: '已批准',
    rejected: '已驳回',
    completed: '已归还'
  }
  return map[status] || status
}

const getStatusTagType = (status) => {
  const map = {
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
    completed: 'info'
  }
  return map[status] || 'info'
}

const formatTime = (date) => dayjs(date).format('HH:mm')

onMounted(() => {
  const userStr = localStorage.getItem('user')
  if (userStr) currentUser.value = JSON.parse(userStr)
  fetchData()
  fetchApps()
  
  window.addEventListener('message-updated', handleMessageUpdated)
})

onUnmounted(() => {
  window.removeEventListener('message-updated', handleMessageUpdated)
})
</script>

<style scoped>
.home-container {
  padding: 0;
}

.welcome-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  background: linear-gradient(135deg, #3370ff 0%, #4080ff 100%);
  border-radius: 12px;
  color: #fff;
}

.welcome-text h1 {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
}

.welcome-text p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
}

.quick-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 48px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 4px;
}

.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.workplace-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f4f5f7;
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #1f2329;
}

.mr-8 {
  margin-right: 8px;
}

.more-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #3370ff;
  cursor: pointer;
  transition: opacity 0.2s;
}

.more-link:hover {
  opacity: 0.75;
}

.app-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 20px;
  padding: 4px 0;
}

.app-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.25s;
}

.app-item:hover {
  transform: translateY(-4px);
}

.app-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: all 0.25s;
}

.app-item:hover .app-icon {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.delete-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background-color: #f56c6c;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.app-item:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  background-color: #f78989;
  transform: scale(1.1);
}

.app-name {
  font-size: 13px;
  color: #646a73;
  text-align: center;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-list {
  display: flex;
  flex-direction: column;
}

.task-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 8px;
  background: #f8f9fa;
}

.task-item:last-child {
  margin-bottom: 0;
}

.task-item:hover {
  background: #f0f2f5;
}

.task-status-bar {
  width: 4px;
  height: 40px;
  border-radius: 2px;
  margin-right: 14px;
  flex-shrink: 0;
}

.task-status-bar.high { background: #f56c6c; }
.task-status-bar.medium { background: #e6a23c; }
.task-status-bar.low { background: #909399; }

.task-info {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-size: 14px;
  color: #1f2329;
  margin-bottom: 4px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-meta {
  font-size: 12px;
  color: #8f959e;
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-icon {
  display: flex;
  align-items: center;
}

.task-tag {
  flex-shrink: 0;
}

.list-container {
  display: flex;
  flex-direction: column;
}

.list-item {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f4f5f7;
  cursor: pointer;
  transition: all 0.2s;
}

.list-item:last-child {
  border-bottom: none;
}

.list-item:hover {
  background: #f8f9fa;
  margin: 0 -24px;
  padding: 14px 24px;
  border-radius: 10px;
}

.avatar {
  flex-shrink: 0;
  margin-right: 14px;
  background: linear-gradient(135deg, #3370ff 0%, #4080ff 100%);
  font-size: 14px;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-size: 14px;
  color: #1f2329;
  margin-bottom: 4px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-title.unread {
  font-weight: 600;
}

.item-desc {
  font-size: 12px;
  color: #8f959e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

.mr-8 {
  margin-right: 8px;
}

.item-time {
  font-size: 12px;
  color: #c0c4cc;
  flex-shrink: 0;
  margin-left: 12px;
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #c0c4cc;
}

.empty-placeholder p {
  margin-top: 12px;
  font-size: 14px;
  color: #8f959e;
}

.mb-6 { margin-bottom: 20px; }
.mt-6 { margin-top: 20px; }

@media (max-width: 768px) {
  .welcome-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 16px;
  }

  .welcome-text h1 {
    font-size: 18px;
    line-height: 1.4;
  }

  .quick-stats {
    width: 100%;
    gap: 12px;
  }

  .stat-item {
    min-width: calc(50% - 6px);
    padding: 10px 0;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 10px;
  }

  .stat-value {
    font-size: 22px;
  }

  .chart-card,
  .workplace-card {
    padding: 16px;
  }

  .chart-card {
    margin-bottom: 16px;
  }

  .card-header {
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }

  .header-title {
    font-size: 15px;
  }

  .more-link {
    font-size: 12px;
  }

  .app-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 12px;
  }

  .app-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 8px;
  }

  .app-name {
    max-width: 72px;
    font-size: 12px;
  }

  .task-item {
    padding: 12px;
  }

  .task-title,
  .item-title {
    white-space: normal;
    line-height: 1.4;
  }

  .task-meta,
  .item-desc {
    white-space: normal;
    line-height: 1.4;
  }

  .list-item {
    align-items: flex-start;
  }

  .list-item:hover {
    margin: 0;
    padding: 14px 0;
    background: transparent;
    border-radius: 0;
  }

  .item-time {
    margin-left: 8px;
    font-size: 11px;
  }
}
</style>
